import { PlusOutlined } from '@ant-design/icons';
import { Button, message, Divider, Tag, Breadcrumb } from 'antd';
import React, { useState, useRef } from 'react';
import { PageContainer, FooterToolbar } from '@ant-design/pro-layout';
import type { ProColumns, ActionType } from '@ant-design/pro-table';
import ProTable from '@ant-design/pro-table';
import { groups, deleteGroups, names as fetchNames } from '@/services/api';
import type { API } from '@/services/typings';
import { t, getTablePaginationConfig, GroupTypeDevice, GroupTypeUser, GrossGropType_UserToDeviceGroup, GrossGropType_UserGroupToDeviceGroup, NamesPurpose_EditDeviceGroup } from '../../global';
import NewOrUpdateForm from './NewOrUpdateForm';
import { Link, useIntl, useModel } from '@umijs/max';
import styles from './index.less';
import { getLocalSortState, handleSortStateChange } from '@/components/Others/table_sort';
import MyModalConfirm from '@/components/Others/MyModalConfirm';
import { LoadingSpinner } from '../GroupList';
import { hasGlobalPermToAssignStrategyToDeviceGroup, hasPermDeviceGroupsEdit, hasPermToViewDevicesInGroup } from '../RoleList/permissionUtils';

export const DEVICE_GROUP_LIST_SORT_STATE_KEY = 'device_group_list_sort_state';

const handleDelete = async (rows: API.GroupListItem[]) => {
  const hide = message.loading(t('Deleting'));
  try {
    await deleteGroups({
      data: rows.map((x) => x.guid),
    });
    hide();

    message.success(t('Deletion is successful'));
    return true;
  } catch (error) {
    hide();
    message.error(
      typeof error == 'string' ? (error as string) : t('Deletion failed, please try again!'),
    );
    return false;
  }
};

export const fetchAllNames = async (pattern: string, table: string, purpose: string, type?: number) => {
  let g: string[] = [];
  let offset = 0;
  const limit = 500;
  let firstItem = undefined;
  do {
    const items = await fetchNames({ table, limit: limit, offset: offset, pattern, type, purpose: purpose })
    const itemsLength = items.length;
    if (itemsLength === 0 || (items[0] === firstItem)/* in case of old backend */) {
      break;
    } else {
      firstItem = items[0];
      g = g.concat(items)
      if (itemsLength < limit) break;
    }
    offset += limit;
  } while (true);
  g.sort();
  return g;
};

export const fetchAllGroups = async (pattern: string, purpose: string) => {
  return await fetchAllNames(pattern, 'grp', purpose, GroupTypeUser);
};

const fetchAllUsers = async (pattern: string, purpose: string) => {
  return await fetchAllNames(pattern, 'user', purpose, undefined);
};

const TableList: React.FC = () => {
  const [createModalVisible, handleModalVisible] = useState<boolean>(false);
  const [updateModalVisible, handleUpdateModalVisible] = useState<boolean>(false);
  const actionRef = useRef<ActionType>();
  const [allGroups, setAllGroups] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<string[]>([]);
  const [currentRow, setCurrentRow] = useState<API.GroupListItem>();
  const [selectedRowsState, setSelectedRows] = useState<API.GroupListItem[]>([]);
  const [sortState, setSortState] = useState<API.SortState>(() => getLocalSortState(DEVICE_GROUP_LIST_SORT_STATE_KEY));
  const [loading, setLoading] = useState<boolean>(false);
  const { initialState } = useModel('@@initialState');
  const canEdit = hasPermDeviceGroupsEdit(initialState?.user);
  const canAssignStrategy = hasGlobalPermToAssignStrategyToDeviceGroup(initialState?.user);

  const fetchDataForForm = async () => {
    if (canEdit) {
      const promises: Promise<void>[] = [];
      promises.push(fetchAllGroups('%', NamesPurpose_EditDeviceGroup).then(grps => setAllGroups(grps)));
      promises.push(fetchAllUsers('%', NamesPurpose_EditDeviceGroup).then(users => setAllUsers(users)));
      await Promise.all(promises);
    }
  };

  const DeleteGroups = (props: { children: any; onClick: () => void }) => (
    <MyModalConfirm
      title={t('deleteConfirm', true, 'Are you sure to delete?')}
      onConfirm={() => props.onClick()}
      okText="Yes"
      cancelText="No"
    >
      {props.children}
    </MyModalConfirm>
  );

  const columns = deviceGroupColumns({
    actionRef,
    showLinkName: (groupname: string) => hasPermToViewDevicesInGroup(initialState?.user, groupname),
    extraColumns: [
      {
        title: t('Action'),
        hideInTable: !canEdit && !canAssignStrategy,
        search: false,
        render: (text, record) => {
          const actions = [
            <a
              key="edit"
              onClick={async () => {
                setCurrentRow(record);
                setLoading(true);
                try {
                  await fetchDataForForm();
                  setLoading(false);
                  handleUpdateModalVisible(true);
                } catch (e) {
                  setLoading(false);
                  message.error(typeof e === 'string' ? e : 'Failed to fetch data.');
                }
              }}
            >
              {t('Edit')}
            </a>,
            canEdit && (
              <DeleteGroups
                key="delete"
                onClick={async () => {
                  setCurrentRow(record);
                  await handleDelete([record]);
                  actionRef.current?.reloadAndRest?.();
                }}
              >
                <a style={{ color: 'red' }}>{t('Delete')}</a>
              </DeleteGroups>
            ),
          ].filter(Boolean);
          return actions.flatMap((action, index) =>
            index < actions.length - 1 ? [action, <Divider key={`d${index}`} type="vertical" />] : [action]
          );
        },
      },
    ],
    sortState,
  });

  return (
    <PageContainer
      breadcrumbRender={() => (
        <Breadcrumb items={[
          { title: t('menu.list.group-list', true) },
          { title: t('menu.list.group-list.device', true) },
        ]} />
      )}
    >
      {loading && (
        <LoadingSpinner />
      )}
      <ProTable<API.GroupListItem, API.PageParams & { type: string }>
        headerTitle={t('Group List')}
        columnsState={{ persistenceType: 'localStorage', persistenceKey: 'device_grouplist_columns_state' }}
        actionRef={actionRef}
        rowKey="guid"
        search={{
          labelWidth: 0,
        }}
        toolBarRender={canEdit ? () => [
          <Button
            type="primary"
            key="primary"
            onClick={async () => {
              setLoading(true);
              try {
                await fetchDataForForm();
                setLoading(false);
                handleModalVisible(true);
              } catch (e) {
                setLoading(false);
                message.error(typeof e === 'string' ? e : 'Failed to fetch data.');
              }
            }}
          >
            <PlusOutlined /> {t('Create')}
          </Button>,
        ] : undefined}
        request={async (params) => {
          const res = await groups(params, undefined, undefined, sortState);
          return res;
        }}
        params={{ type: GroupTypeDevice.toString() }}
        columns={columns}
        rowSelection={canEdit ? {
          onChange: (_, selectedRows) => {
            setSelectedRows(selectedRows);
          },
        } : undefined}
        pagination={getTablePaginationConfig("device_group_list")}
        onChange={(_, __, sorter) => {
          handleSortStateChange(sorter, actionRef, DEVICE_GROUP_LIST_SORT_STATE_KEY, setSortState);
        }}
      />
      {selectedRowsState?.length > 0 && (
        <FooterToolbar
          extra={
            <div>
              {t('Chosen')} <a style={{ fontWeight: 600 }}>{selectedRowsState.length}</a>{' '}
              {t('Items')}
            </div>
          }
        >
          <DeleteGroups
            onClick={async () => {
              await handleDelete(selectedRowsState);
              setSelectedRows([]);
              actionRef.current?.reloadAndRest?.();
            }}
          >
            <Button danger>{t('Delete')}</Button>
          </DeleteGroups>
        </FooterToolbar>
      )}
      <NewOrUpdateForm
        isNew={true}
        visible={createModalVisible}
        allGroups={allGroups}
        allUsers={allUsers}
        canEdit={canEdit}
        onVisibleChange={handleModalVisible}
        actionRef={actionRef}
        initialValues={undefined}
        handleModalVisible={handleModalVisible}
        handleUpdateModalVisible={handleUpdateModalVisible}
      />
      <NewOrUpdateForm
        isNew={false}
        visible={updateModalVisible}
        allGroups={allGroups}
        allUsers={allUsers}
        canEdit={canEdit}
        onVisibleChange={handleUpdateModalVisible}
        actionRef={actionRef}
        initialValues={currentRow || undefined}
        handleModalVisible={handleModalVisible}
        handleUpdateModalVisible={handleUpdateModalVisible}
      />
    </PageContainer>
  );
};

export default TableList;


export const deviceGroupColumns = (props: { actionRef: any, showLinkName: (groupname: string) => boolean, extraColumns?: ProColumns<API.GroupListItem>[], sortState: API.SortState }) => {
  const intl = useIntl();
  const plain = (text: string, defaultMessage = '') => t(`pages.GroupList.${text}`, true, defaultMessage || text, intl) as string;

  const userTagColor = '#3674B5';
  const userGroupTagColor = '#399C97';
  const makeUserRules = (record: API.GroupListItem) => record.accessed_from.filter((x) => x.type == GrossGropType_UserToDeviceGroup).length > 0 && <>
    <div>
      &nbsp;
      {
        record.accessed_from.filter((x) => x.type == GrossGropType_UserToDeviceGroup).map((x) => x.name).sort().map((x) => {
          return <Tag key={'outgoings'} color={userTagColor} className={styles.listCrossGroupItems}>{x}</Tag>;
        })
      }
    </div>
  </>;

  const makeUserGroupRules = (record: API.GroupListItem) => record.accessed_from.filter((x) => x.type == GrossGropType_UserGroupToDeviceGroup).length > 0 && <>
    <div>
      &nbsp;
      {
        record.accessed_from.filter((x) => x.type == GrossGropType_UserGroupToDeviceGroup).map((x) => x.name).sort().map((x) => {
          return <Tag key={'incomings'} color={userGroupTagColor} className={styles.listCrossGroupItems}>{x}</Tag>;
        })
      }
    </div>
  </>;

  const columns: ProColumns<API.GroupListItem>[] = [
    {
      title: t('Name'),
      dataIndex: 'name',
      render: (text, record) => (
        !props.showLinkName(record.name) ? text : <Link to={`/groups/device/${record.guid}`}>{text}</Link>
      ),
      key: 'name',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'name' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: t('Note'),
      dataIndex: 'note',
      valueType: 'textarea',
      search: false,
      key: 'note',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'note' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: <div>
        {t('Cross-group connections', true)}
        <div style={{ height: '8px' }} />
        <Tag color={userGroupTagColor} className={styles.titleTag}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {plain('User Group')}
          </div>
        </Tag>
        <Tag color={userTagColor} className={styles.titleTag}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {plain('User')}
          </div>
        </Tag>
      </div>,
      search: false,
      render: (text, record) => {
        return <>
          {makeUserGroupRules(record)}
          {
            record.accessed_from.filter((x) => x.type == GrossGropType_UserGroupToDeviceGroup).length > 0
            && record.accessed_from.filter((x) => x.type == GrossGropType_UserToDeviceGroup).length > 0
            && <div style={{ height: '8px' }} />
          }
          {makeUserRules(record)}
        </>;
      },
    },
    {
      title: t('Strategy', true),
      dataIndex: 'strategy_name',
      tooltip: `-: ${t('Default Strategy', true)}`,
      key: 'c_name',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'c_name' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    ...props.extraColumns || [],
  ];
  return columns;
}
